/**
 * @file column-controls.tsx
 * @description Управление боковыми колонками конструктора (phase19.16, партия
 * после приёмки Р2а): ручка ширины колонки, кнопка «Свернуть» и свёрнутая
 * полоска 48 px с кнопкой «Развернуть».
 *
 * Полоску можно развернуть кнопкой, двойным щелчком или потянув к канве —
 * тогда колонка открывается с минимальной шириной. После свёртки фокус
 * переходит на «Развернуть» той же колонки (`autoFocus`).
 */

import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from "lucide-react";
import { useEffect, useRef, type PointerEvent } from "react";

import { Button } from "../../ui/button";
import { ResizeHandle } from "../../ui/ResizeHandle";
import type { BuilderColumnWidths } from "../../../hooks/site-builder/useBuilderColumnWidths";
import { COLLAPSE_PULL_PX, KEYBOARD_STEP_PX, type BuilderColumnKey } from "../../../lib/site-builder/column-widths";
import { cn } from "@/lib/utils";

/** Сторона колонки относительно канвы. */
export type ColumnSide = "left" | "right";

/** Что нужно раскладке от рабочей области: ширины и свёртка с переводом фокуса. */
export interface ColumnsApi {
  widths: BuilderColumnWidths;
  /** Свернуть и поставить фокус на «Развернуть» этой колонки. */
  collapse: (key: BuilderColumnKey) => void;
  /** Колонка, которую только что свернули (её полоска забирает фокус). */
  focusKey: BuilderColumnKey | null;
}

/** Подписи ручек для чтеца экрана по содержимому колонки. */
export const HANDLE_LABELS = { nav: "Ширина навигатора", panel: "Ширина панели настроек" } as const;

/** Ручки колонок; справа от колонки тянет её вправо (1), слева — влево (-1). */
const HANDLE_META: Record<BuilderColumnKey, { label: string; direction: 1 | -1 }> = {
  nav: { label: HANDLE_LABELS.nav, direction: 1 },
  // На планшете в колонке то навигатор, то панель — подпись передаёт раскладка (`label`).
  side: { label: HANDLE_LABELS.nav, direction: 1 },
  panel: { label: HANDLE_LABELS.panel, direction: -1 },
};

/** Сдвиг к канве, после которого полоска разворачивается, px. */
const STRIP_PULL_PX = 16;

/** Ручка ширины развёрнутой колонки; протянули за минимум — свёртка. `label` — своя подпись (что сейчас в колонке). */
export function ColumnHandle({ api, column, label }: { api: ColumnsApi; column: BuilderColumnKey; label?: string }) {
  const { widths } = api;
  const col = widths.columns[column];
  if (!col || col.collapsed) return null;
  return (
    <ResizeHandle
      {...HANDLE_META[column]}
      label={label ?? HANDLE_META[column].label}
      value={col.width}
      min={col.min}
      max={col.max}
      step={KEYBOARD_STEP_PX}
      collapsePull={COLLAPSE_PULL_PX}
      onLive={(px) => widths.live(column, px)}
      onCommit={(px) => widths.commit(column, px)}
      onReset={() => widths.reset(column)}
      onCollapse={() => api.collapse(column)}
    />
  );
}

/** Кнопка «Свернуть» в шапке или углу колонки; до 1024 — 48 px под палец. */
export function CollapseButton({ side, label, onClick, className }: { side: ColumnSide; label: string; onClick: () => void; className?: string }) {
  const Icon = side === "left" ? PanelLeftClose : PanelRightClose;
  return (
    <Button variant="ghost" size="icon-sm" aria-label={label} title={label} onClick={onClick} className={cn("max-lg:size-12", className)}>
      <Icon aria-hidden />
    </Button>
  );
}

interface StripProps {
  side: ColumnSide;
  /** «Показать структуру сайта» / «Показать панель настроек». */
  label: string;
  /** Подпись вдоль полоски. */
  caption: string;
  /** Минимальная ширина колонки — с ней разворачиваемся, если тянули. */
  minWidth: number;
  autoFocus: boolean;
  onExpand: (width?: number) => void;
}

/** Свёрнутая колонка: полоска 48 px, кнопка «Развернуть» и подпись вдоль. */
export function CollapsedStrip({ side, label, caption, minWidth, autoFocus, onExpand }: StripProps) {
  const button = useRef<HTMLButtonElement | null>(null);
  const start = useRef<{ id: number; x: number } | null>(null);
  const Icon = side === "left" ? PanelLeftOpen : PanelRightOpen;
  // К канве: от левой полоски — вправо, от правой — влево.
  const toward = side === "left" ? 1 : -1;

  useEffect(() => {
    if (autoFocus) button.current?.focus();
  }, [autoFocus]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    start.current = { id: e.pointerId, x: e.clientX };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const s = start.current;
    if (!s || s.id !== e.pointerId) return;
    if (toward * (e.clientX - s.x) < STRIP_PULL_PX) return;
    start.current = null;
    onExpand(minWidth);
  };

  return (
    <div
      data-testid={`collapsed-${side}`}
      title="Потяните к канве или дважды щёлкните, чтобы развернуть"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => (start.current = null)}
      onPointerCancel={() => (start.current = null)}
      onDoubleClick={() => onExpand()}
      className={cn(
        "flex min-h-0 cursor-col-resize touch-none select-none flex-col items-center gap-3 overflow-hidden py-3",
        side === "left" ? "[box-shadow:inset_-1px_0_0_var(--outline-variant)]" : "[box-shadow:inset_1px_0_0_var(--outline-variant)]",
      )}
    >
      <Button ref={button} variant="ghost" size="icon" aria-label={label} title={label} onClick={() => onExpand()} className="max-lg:size-12">
        <Icon aria-hidden />
      </Button>
      <span aria-hidden className="type-caption whitespace-nowrap text-on-surface-variant [writing-mode:vertical-rl]">
        {caption}
      </span>
    </div>
  );
}
